import { Trans, useLingui } from '@lingui/react/macro'
import { Anchor, Card, Center, Group, Stack, Text, Title } from '@mantine/core'
import type { AttachmentResponse } from '@markku/types'
import { formatDate } from '@markku/utils'
import { IconFile, IconFiles } from '@tabler/icons-react'

export type DocEntry = AttachmentResponse & {
  source: 'property' | 'visit'
  date: string | Date
}

export function DocumentsSection({
  certificates,
  visitReports,
  otherDocuments,
}: {
  certificates: DocEntry[]
  visitReports: DocEntry[]
  otherDocuments: DocEntry[]
}) {
  const { t } = useLingui()
  const hasDocuments =
    certificates.length > 0 ||
    visitReports.length > 0 ||
    otherDocuments.length > 0

  return (
    <Stack gap="md">
      <Title order={4}>
        <Trans>Documents</Trans>
      </Title>

      {!hasDocuments ? (
        <Card withBorder radius="md" p="xl" bg="gray.0">
          <Center>
            <Stack align="center" gap="xs">
              <IconFiles
                size={48}
                stroke={1.5}
                color="var(--mantine-color-gray-5)"
              />
              <Text c="dimmed" fw={500}>
                <Trans>No documents</Trans>
              </Text>
            </Stack>
          </Center>
        </Card>
      ) : (
        <Stack gap="lg">
          {certificates.length > 0 && (
            <DocumentGroup title={t`Certificates`}>
              {certificates.map((doc) => (
                <DocumentCard
                  key={doc.id}
                  href={doc.downloadUrl}
                  label={getCertificateLabel(doc, t)}
                  date={doc.date}
                />
              ))}
            </DocumentGroup>
          )}

          {visitReports.length > 0 && (
            <DocumentGroup title={t`Visit reports`}>
              {visitReports.map((doc) => (
                <DocumentCard
                  key={doc.id}
                  href={doc.downloadUrl}
                  label={doc.filename || t`Visit report`}
                  date={doc.date}
                />
              ))}
            </DocumentGroup>
          )}

          {otherDocuments.length > 0 && (
            <DocumentGroup title={t`Other documents`}>
              {otherDocuments.map((doc) => (
                <DocumentCard
                  key={doc.id}
                  href={doc.downloadUrl}
                  label={doc.filename || t`Document`}
                  date={doc.date}
                  description={doc.description}
                />
              ))}
            </DocumentGroup>
          )}
        </Stack>
      )}
    </Stack>
  )
}

function getCertificateLabel(att: AttachmentResponse, t: (s: any) => string) {
  if (att.description) return att.description
  const certType = att.metadata?.certificateType
  if (certType === 'nuohoustodistus') return t`Chimney sweep certificate`
  if (certType === 'mittauspöytäkirja') return t`Measurement report`
  return att.filename || t`Certificate`
}

function DocumentGroup({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <Stack gap="sm">
      <Text fw={600} size="sm" c="dimmed" tt="uppercase">
        {title}
      </Text>
      <Stack gap="xs">{children}</Stack>
    </Stack>
  )
}

function DocumentCard({
  href,
  label,
  date,
  description,
}: {
  href?: string | null
  label: string
  date: string | Date
  description?: string | null
}) {
  return (
    <Card withBorder p="xs" radius="md">
      <Anchor
        href={href || undefined}
        target="_blank"
        rel="noopener noreferrer"
        underline="never"
        c="inherit"
      >
        <Group gap="xs">
          <IconFile size={20} />
          <Stack gap={0} style={{ flex: 1 }}>
            <Text size="sm" fw={500}>
              {label}
            </Text>
            <Text size="xs" c="dimmed">
              {formatDate(new Date(date))}
              {description ? ` — ${description}` : ''}
            </Text>
          </Stack>
        </Group>
      </Anchor>
    </Card>
  )
}
